import { z } from 'zod';
import { DateOrIsoStringSchema } from './date-or-iso';

/**
 * Wire shapes for the usage dashboard API (`src/api/usage-dashboard.ts`),
 * read by `AdminUsageDashboard.tsx` through `fetchJson`. The raw-SQL row
 * schemas stay in the route: they describe what Postgres returns, these
 * describe what the route sends. Dates are Date server-side and ISO strings
 * on the wire; `readRate` is a plain number on both sides.
 */

export const UsageStatisticsResponseSchema = z.object({
  totalArtifacts: z.number(),
  readCount: z.number(),
  readRate: z.number(),
  uniqueReps: z.number(),
  earliestDate: DateOrIsoStringSchema.nullable(),
});

export const UsageByTypeEntrySchema = z.object({
  artifactType: z.string(),
  count: z.number(),
  readCount: z.number(),
});

export type UsageByTypeEntry = z.infer<typeof UsageByTypeEntrySchema>;

export const UsageByTypeResponseSchema = z.object({
  types: z.array(UsageByTypeEntrySchema),
  total: z.number(),
  readTotal: z.number(),
});

export const UsageTypeActionSchema = z.object({ actionId: z.string(), action: z.string(), count: z.number() });

export const UsageTypeActionsResponseSchema = z.object({
  actions: z.array(UsageTypeActionSchema),
  typeTotal: z.number(),
  actionedArtifacts: z.number(),
  noActionCount: z.number(),
});

export const UsageDailySchema = z.object({
  day: DateOrIsoStringSchema,
  total: z.number(),
  readCount: z.number(),
  readRate: z.number(),
});

export const UsageDailyResponseSchema = z.object({ daily: z.array(UsageDailySchema) });

export const UsageByRepSchema = z.object({
  ownerEmail: z.string().nullable(),
  totalArtifacts: z.number(),
  readCount: z.number(),
  readRate: z.number(),
  lastReadAt: DateOrIsoStringSchema.nullable(),
});

export const UsageByRepResponseSchema = z.object({ reps: z.array(UsageByRepSchema) });

export const UsageRepDetailSchema = z.object({
  id: z.number(),
  artifactUuid: z.string().nullable(),
  artifactName: z.string().nullable(),
  artifactType: z.string().nullable(),
  timestamp: DateOrIsoStringSchema.nullable(),
  read: z.boolean().nullable(),
  readAt: DateOrIsoStringSchema.nullable(),
});

export const UsageRepDetailResponseSchema = z.object({ artifacts: z.array(UsageRepDetailSchema) });

export const UsageArtifactTypesResponseSchema = z.object({ types: z.array(z.string()) });
